import {
  getPaymentMethodWithSettings,
  getMoneyGram,
  deletePaymentAccount,
  addMoneyGramAccount,
  savePaymentMethod,
  updatePaymentAccount,
  saveCustomerPlaidItem,
  getPlaidInactiveAccounts,
} from '../../api/api'
import { localStorage as local } from '@entrata/utils'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { addError } from 'portal-modules/common/redux/modules/errors'
import getProjectName from 'portal-modules/common/utils/get-project-name'
import { postAutoPaymentDeleted } from './autoPayments'

// action types
const REQUEST_PAYMENT_METHODS = 'rp/paymentMethods/REQUEST_PAYMENT_METHODS'
const RECEIVE_PAYMENT_METHODS = 'rp/paymentMethods/RECEIVE_PAYMENT_METHODS'
const DELETE_PAYMENT_METHOD = 'rp/paymentMethods/DELETE_PAYMENT_METHOD'
const DELETE_PAYMENT_METHOD_FROM_UPDATE =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_FROM_UPDATE'
const PAYMENT_METHOD_DELETED = 'rp/paymentMethods/PAYMENT_METHOD_DELETED'
const ADD_PAYMENT_METHOD = 'rp/paymentMethods/ADD_PAYMENT_METHOD'
const PAYMENT_METHOD_ADDED = 'rp/paymentMethods/PAYMENT_METHOD_ADDED'
const UPDATE_PAYMENT_METHOD = 'rp/paymentMethods/UPDATE_PAYMENT_METHOD'
const PAYMENT_METHOD_UPDATED = 'rp/paymentMethods/PAYMENT_METHOD_UPDATED'
const CREATE_MONEYGRAM_ACCOUNT = 'rp/paymentMethods/CREATE_MONEYGRAM_ACCOUNT'
const MONEYGRAM_ACCOUNT_CREATED = 'rp/paymentMethods/MONEYGRAM_ACCOUNT_CREATED'
const DELETE_PAYMENT_METHOD_WITH_PAYMENT =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_WITH_PAYMENT'
const ADD_CUSTOMER_PLAID_ITEM = 'rp/paymentMethods/ADD_CUSTOMER_PLAID_ITEM'
const REQUEST_PLAID_INACTIVE_ACCOUNTS =
  'rp/paymentMethods/REQUEST_PLAID_INACTIVE_ACCOUNTS'
const RECEIVE_PLAID_INACTIVE_ACCOUNTS =
  'rp/paymentMethods/RECEIVE_PLAID_INACTIVE_ACCOUNTS'
const DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT'
const DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT =
  'rp/paymentMethods/DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT'

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_PAYMENT_METHODS:
      return { ...state, ...payload }
    case RECEIVE_PLAID_INACTIVE_ACCOUNTS:
      return {
        ...state,
        ['plaid_inactive_accounts']: { ...payload },
      }
    case DELETE_PAYMENT_METHOD_FROM_UPDATE:
    case PAYMENT_METHOD_DELETED:
      return Object.values(state).reduce((obj, method) => {
        if (method.id === payload) return obj
        return { ...obj, [method.id]: method }
      }, {})
    case PAYMENT_METHOD_ADDED: {
      // Check if the newly added payment method is marked as default
      if (payload?.is_default_account) {
        // Create new state object with updated default status
        // Iterate through all existing payment methods and update their default status
        const updatedState = Object.entries(state).reduce((acc, [id, method]) => ({
          ...acc,
          [id]: {
            ...method,
            // If method was previously default, set it to false
            // Otherwise keep its current default status
            is_default_account: method.is_default_account ? false : method.is_default_account
          }
        }), {});
        
        // Return new state with all existing methods updated and new method added
        return { ...updatedState, [payload.id]: { ...payload } };
      }
      
      // If new method is not default, simply add it to state without changes
      return { ...state, [payload.id]: { ...payload } };
    }
    case PAYMENT_METHOD_UPDATED:
      return {
        ...state,
        [payload.id]: { ...(state[payload.id] || {}), ...payload },
      }
    case MONEYGRAM_ACCOUNT_CREATED:
      return Object.values(state).reduce((obj, method) => {
        if (
          method.id === 'moneygram' &&
          !method.hasAccount &&
          method.payment_type_id === 10
        ) {
          return { ...obj, [payload.id]: { ...payload, hasAccount: true } }
        } else {
          return { ...obj, [method.id]: method }
        }
      }, {})
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const requestPaymentMethods = (payload = {}) => ({
  type: REQUEST_PAYMENT_METHODS,
  payload,
})

export const receivePaymentMethods = payload => ({
  type: RECEIVE_PAYMENT_METHODS,
  payload,
})

export const requestPlaidInactiveAccounts = () => ({
  type: REQUEST_PLAID_INACTIVE_ACCOUNTS,
})

export const receivePlaidInactiveAccounts = payload => ({
  type: RECEIVE_PLAID_INACTIVE_ACCOUNTS,
  payload,
})

export const deletePaymentMethod = (payload, callback) => ({
  type: DELETE_PAYMENT_METHOD,
  callback,
  payload,
})

export const deletePaymentMethodWithPayments = (payload, callback) => ({
  type: DELETE_PAYMENT_METHOD_WITH_PAYMENT,
  callback,
  payload,
})

export const createMoneyGramAccount = payload => ({
  type: CREATE_MONEYGRAM_ACCOUNT,
  payload,
})

export const addCustomerPlaidItem = payload => ({
  type: ADD_CUSTOMER_PLAID_ITEM,
  payload,
})

export const addPaymentMethod = createFormAction(ADD_PAYMENT_METHOD)
export const forceStorePaymentMethod = (payload, callback) => ({
  type: ADD_PAYMENT_METHOD,
  callback,
  payload,
})

export const updatePaymentMethod = createFormAction(UPDATE_PAYMENT_METHOD)

export const deletePaymentMethodWithRefundAccount = (payload, callback) => ({
  type: DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT,
  callback,
  payload,
})

export const deletePaymentMethodWithPaymentsAndRefundAccount = (
  payload,
  callback,
) => ({
  type: DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT,
  callback,
  payload,
})

export function* handlePaymentMethodError({ message, fields }, { key, action }) {
  const fieldMessage = Array.isArray(fields)
    ? fields.find(field => field?.message)?.message
    : undefined
  message = fieldMessage || message

  action.payload &&
    'function' === typeof action.payload.onError &&
    action.payload.onError()
  yield put(addError({ key, message }))
}

// sagas
function* fetchPaymentMethods({ payload }) {
  let moneygram_account = null
  let paymentMethods = null
  const { callback = null } = payload
  if ('rh' === getProjectName()) {
    paymentMethods = yield call(getPaymentMethodWithSettings)
  } else {
    ;[paymentMethods, { moneygram_account }] = yield all([
      call(getPaymentMethodWithSettings),
      call(getMoneyGram),
    ])
  }

  const { settings, payment_accounts } = paymentMethods

  let allPaymentMethods = [...payment_accounts]
  if (moneygram_account) {
    allPaymentMethods = [
      ...payment_accounts,
      {
        ...moneygram_account,
        id: moneygram_account.moneygram_account_number,
        payment_type_id: 10,
        hasAccount: true,
      },
    ]
  }

  if (Array.isArray(allPaymentMethods)) {
    yield put(
      receivePaymentMethods(
        allPaymentMethods.reduce(
          (obj, method) => ({
            ...obj,
            [method.id]: method,
          }),
          {},
        ),
      ),
    )
  }
  yield put(addSettings({ paymentMethods: settings }))
  yield callback && typeof callback == 'function' && callback()
}

function* postDeletePaymentMethod({ payload, callback }) {
  yield call(deletePaymentAccount, {
    payment_account_id: payload,
  })

  yield put({ type: PAYMENT_METHOD_DELETED, payload })

  const { autoPayments } = yield select()
  const connectedAutoPayments = Object.values(autoPayments).filter(
    ({ customer_payment_account_id }) => payload == customer_payment_account_id,
  )
  if (Object.keys(connectedAutoPayments).length) {
    yield all(
      Object.values(connectedAutoPayments).map(({ id }) =>
        put(postAutoPaymentDeleted(id)),
      ),
    )
  }

  typeof callback == 'function' && callback()
}

function* postCreateMoneyGramAccount() {
  const { moneygram_account } = yield call(addMoneyGramAccount)

  if (moneygram_account) {
    let accountDetails = {
      ...moneygram_account,
      id: moneygram_account.moneygram_account_number,
      payment_type_id: 10,
    }

    yield put({
      type: MONEYGRAM_ACCOUNT_CREATED,
      payload: { ...accountDetails },
    })
  }
}

function* postAddPaymentMethod({ payload }) {
  let nextPayload = {}
  const {
    settings: { payments },
    userSettings,
    properties,
  } = yield select()
  const isUSClient = 'US' === properties[userSettings?.propertyId]?.country_code
  const isAllowPlaid = payments && payments.plaid_account_verification

  const addedMethod = yield call(savePaymentMethod, payload)
  nextPayload = addedMethod

  yield put({
    type: PAYMENT_METHOD_ADDED,
    payload: addedMethod,
  })

  // get updated list of inactive accounts
  if (addedMethod.payment_type_id == 4 && isUSClient && isAllowPlaid) {
    yield put(requestPlaidInactiveAccounts())
  }

  if (typeof payload.callback === 'function') {
    yield call(payload.callback, nextPayload)
  }
}

function* editPaymentMethod({ payload }) {
  const method = yield call(updatePaymentAccount, payload)

  yield put({
    type: PAYMENT_METHOD_UPDATED,
    payload: method,
  })

  if (payload.id !== method.id) {
    yield put({ type: DELETE_PAYMENT_METHOD_FROM_UPDATE, payload: payload.id })
  }
}

function* postAddCustomerPlaidItem({ payload }) {
  const { accounts } = yield call(saveCustomerPlaidItem, payload)

  if (accounts && accounts.length) {
    yield all(
      accounts.map(account =>
        put({
          type: PAYMENT_METHOD_ADDED,
          payload: account,
        }),
      ),
    )
  }

  if (payload.is_relink_account) {
    window.location.reload()
  }

  // get updated list of inactive accounts always after saving customer plaid accounts
  yield put(requestPlaidInactiveAccounts())

  yield local.get('isPlaidLinkRefresh') && local.remove('isPlaidLinkRefresh')

  if (typeof payload.callback === 'function') {
    yield call(payload.callback, accounts)
  }
}

function* fetchPlaidInactiveAccounts() {
  const { plaid_inactive_accounts } = yield call(getPlaidInactiveAccounts)

  yield put(receivePlaidInactiveAccounts(plaid_inactive_accounts))
}

function* watchAddPaymentMethod() {
  yield takeEvery(
    ADD_PAYMENT_METHOD,
    createRequestSaga(postAddPaymentMethod, {
      key: 'paymentMethods',
      message: __('Adding new payment method'),
      successMessage: __('Payment method added'),

      onError: handlePaymentMethodError,
    }),
  )
}

function* watchUpdatePaymentMethod() {
  yield takeEvery(
    UPDATE_PAYMENT_METHOD,
    createRequestSaga(editPaymentMethod, {
      key: 'paymentMethods',
      message: __('Syncing updates'),
      successMessage: __('Payment method updated'),

      onError: handlePaymentMethodError,
    }),
  )
}

function* watchRequestPaymentMethods() {
  yield takeLatest(
    REQUEST_PAYMENT_METHODS,
    createRequestSaga(fetchPaymentMethods, {
      key: 'requestPaymentMethods',
      message: __('Finding payment methods'),
    }),
  )
}

function* watchDeletePaymentMethod() {
  yield takeEvery(
    DELETE_PAYMENT_METHOD,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Syncing updates'),
      successMessage: __('Payment method deleted'),
    }),
  )
}

function* watchDeletePaymentMethodWithPayments() {
  yield takeEvery(
    DELETE_PAYMENT_METHOD_WITH_PAYMENT,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Deleting Payment Method'),
      successMessage: __(
        'The auto-payment(s) and card associated have been deleted.',
      ),
    }),
  )
}

function* watchCreateMoneyGramAccount() {
  yield takeEvery(
    CREATE_MONEYGRAM_ACCOUNT,
    createRequestSaga(postCreateMoneyGramAccount, {
      key: 'payments',
      message: __('Creating account'),
      successMessage: __('MoneyGram account created.'),
    }),
  )
}

function* watchAddCustomerPlaidItemRequest() {
  yield takeEvery(
    ADD_CUSTOMER_PLAID_ITEM,
    createRequestSaga(postAddCustomerPlaidItem, {
      key: 'paymentMethods',
      messgae: __('Syncing updates'),
      successMessage: __('Payment method added'),
      duration: 500,

      onError: handlePaymentMethodError,
    }),
  )
}

function* watchRequestPlaidInactiveAccounts() {
  yield takeEvery(
    REQUEST_PLAID_INACTIVE_ACCOUNTS,
    createRequestSaga(fetchPlaidInactiveAccounts, {
      key: 'paymentMethods',
      message: __('Retrieving bank account options'),
    }),
  )
}

function* watchDeletePaymentMethodWithRefundAccount() {
  yield takeEvery(
    DELETE_PAYMENT_METHOD_WITH_REFUND_ACCOUNT,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Deleting Payment Method'),
      successMessage: __(
        'The refund method and card associated have been deleted.',
      ),
    }),
  )
}

function* watchDeletePaymentMethodWithPaymentsAndRefundAccount() {
  yield takeEvery(
    DELETE_PAYMENT_METHOD_WITH_PAYMENTS_AND_REFUND_ACCOUNT,
    createRequestSaga(postDeletePaymentMethod, {
      key: 'paymentMethods',
      message: __('Deleting Payment Method'),
      successMessage: __(
        'The auto-payment(s), refund method and card associated have been deleted.',
      ),
    }),
  )
}

export const sagas = [
  watchRequestPaymentMethods,
  watchAddPaymentMethod,
  watchDeletePaymentMethod,
  watchUpdatePaymentMethod,
  watchCreateMoneyGramAccount,
  watchDeletePaymentMethodWithPayments,
  watchAddCustomerPlaidItemRequest,
  watchRequestPlaidInactiveAccounts,
  watchDeletePaymentMethodWithRefundAccount,
  watchDeletePaymentMethodWithPaymentsAndRefundAccount,
]
